import { useObserver } from "mobx-react";
import React, { useState } from "react";
import { IconButton, ListItemIcon, makeStyles, Menu, MenuItem } from "@material-ui/core";
import { ExitToApp, MoreVert, SupervisorAccount, VpnKey } from "@material-ui/icons";
import Typography from "@material-ui/core/Typography";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { store } from "@store";
import Avatar from "@components/Avatar";
import { useSidebarLock } from "../../context/sidebar-lock-context";

type Props = {
  onLogout?: () => void;
  onLoginAs?: (e: React.SyntheticEvent<any>) => void;
  onApiTokens?: () => void;
};

export function UserBar(props: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { user } = store();
  const { myProfile } = user;
  const classes = useStyle({});
  const { lock, unlock } = useSidebarLock();

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
    lock();
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    unlock();
  };

  return useObserver(() => (
    <div className={classes.root}>
      <div
        className={classes.userInfo}
        onClick={handleOpenMenu}
      >
        <Avatar src={myProfile.AvatarPic} name={myProfile.Name} size="small" />

        <Typography component="span" className={classes.userName} data-test="UserName">
          {myProfile.Name}
        </Typography>

        <IconButton size="small" className={classes.menuIcon}>
          <MoreVert fontSize="small" />
        </IconButton>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.menuPaper }}
      >
        {props.onLoginAs && (
          <MenuItem
            onClick={(e) => {
              handleCloseMenu();
              props.onLoginAs(e);
            }}
            className={classes.menuItem}
            data-test="LoginAsMenuItem"
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <SupervisorAccount fontSize="small" />
            </ListItemIcon>
            {locale.LogInAs}...
          </MenuItem>
        )}

        {props.onApiTokens && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              props.onApiTokens();
            }}
            className={classes.menuItem}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <VpnKey fontSize="small" />
            </ListItemIcon>
            API Tokens
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleCloseMenu();
            props.onLogout?.();
          }}
          className={classes.menuItem}
          data-test="LogoutMenuItem"
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          {locale.LogOut}
        </MenuItem>
      </Menu>
    </div>
  ));
}

const useStyle = makeStyles((theme: ThemeType) => ({
  root: {
    width: "100%",
    boxSizing: "border-box"
  },

  userInfo: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    cursor: "pointer",
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px ${theme.spacing(1)}px 12px`,
    transition: "background-color 150ms ease",

    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)"
    },

    "& > div:first-child": {
      marginRight: 12,
      flexShrink: 0
    }
  },

  userName: {
    flex: 1,
    minWidth: 0
  },

  menuIcon: {
    flexShrink: 0,
    marginLeft: "auto",
    opacity: 0.5
  },

  menuPaper: {
    minWidth: 200,
    borderRadius: 8,
    marginTop: 4,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
  },

  menuItem: {
    fontSize: 14,
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary
  },

  menuItemIcon: {
    minWidth: 32,
    color: theme.palette.text.secondary
  },

}));
